import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiCreditCard, FiShoppingBag, FiFileText, FiCheckCircle, FiAlertCircle,
    FiClock, FiArrowRight, FiCalendar, FiMessageCircle, FiSmile,
    FiChevronLeft, FiChevronRight, FiSmartphone,
} from 'react-icons/fi';
import { getProfileApi } from '../../Api';
import { notify } from '../../components/Notify';
import { getUENUser, isAuthenticatedUEN } from '../../utils';
import { GooglePlayButton, AppleStoreButton } from '../../components/StoreButtons';

// Imagens dos quadros. Pra trocar por uma foto real: sobrescreva o arquivo SVG
// mantendo o nome OU mude o import abaixo pra apontar pra outro asset.
import bannerHero from '../Home2/assets/card1.jpg';
import homemfeliz from './homem-feliz.png';
import bannerImg2 from '../Home2/assets/mulherfundo.jpg';
import bannerImg3 from '../Home2/assets/suportmulher.jpg';
import promoCardImg from '../../assets/dashboard/estudantehappy.jpg';

// Imagem do lado direito da propaganda do app. Usando card1.jpg por enquanto
// (é o mesmo asset do bannerHero). Pra trocar, aponte pra outro import.
const APP_PROMO_IMG = bannerHero;

const WHATSAPP_NUMBER = '5531997333998';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    'Olá! Preciso de ajuda com a UEN Estudantil.'
)}`;

// Banners do carrossel em destaque. Máximo de 3 (o .slice garante o limite).
// Pra trocar uma imagem: mude o import correspondente lá em cima. Pra usar
// menos que 3, basta remover itens daqui — o carrossel se adapta sozinho
// (com 1 imagem ele vira um banner estático, sem setas/dots).
const BANNERS = [
    { src: bannerHero, alt: 'Banner UEN 1' },
    { src: bannerImg2, alt: 'Banner UEN 2' },
    { src: bannerImg3, alt: 'Banner UEN 3' },
].slice(0, 3);

// Carrossel do banner em destaque. Autoplay a cada 5s (pausável no hover),
// setas e dots clicáveis. Slide via translateX no track.
function BannerCarousel({ images }) {
    const slides = (images || []).slice(0, 3);
    const total = slides.length;
    const [idx, setIdx] = useState(0);
    const [pausado, setPausado] = useState(false);

    // Reinicia o timer sempre que o índice muda ou o autoplay é pausado.
    useEffect(() => {
        if (total <= 1 || pausado) return undefined;
        const t = setTimeout(() => setIdx((i) => (i + 1) % total), 5000);
        return () => clearTimeout(t);
    }, [idx, total, pausado]);

    if (total === 0) return null;

    const go = (i) => setIdx((i + total) % total);

    return (
        <section
            style={carousel.frame}
            aria-label="Banner em destaque"
            onMouseEnter={() => setPausado(true)}
            onMouseLeave={() => setPausado(false)}
        >
            <div style={{ ...carousel.track, transform: `translateX(-${idx * 100}%)` }}>
                {slides.map((b, i) => (
                    <img key={i} src={b.src} alt={b.alt || `Banner ${i + 1}`} style={carousel.image} />
                ))}
            </div>

            {total > 1 && (
                <>
                    <button type="button" aria-label="Banner anterior"
                        style={{ ...carousel.arrow, left: 12 }} onClick={() => go(idx - 1)}>
                        <FiChevronLeft size={20} />
                    </button>
                    <button type="button" aria-label="Próximo banner"
                        style={{ ...carousel.arrow, right: 12 }} onClick={() => go(idx + 1)}>
                        <FiChevronRight size={20} />
                    </button>
                    <div style={carousel.dots}>
                        {slides.map((_, i) => (
                            <button key={i} type="button" aria-label={`Ir para o banner ${i + 1}`}
                                onClick={() => go(i)}
                                style={{ ...carousel.dot, ...(i === idx ? carousel.dotActive : null) }} />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}

const carousel = {
    frame: {
        position: 'relative',
        width: '100%',
        borderRadius: 20,
        overflow: 'hidden',
        marginBottom: '1.5rem',
        aspectRatio: '1200 / 350',
        background: '#F1F5F9',
        boxShadow: '0 4px 12px -2px rgba(15, 23, 42, 0.08)',
    },
    track: {
        display: 'flex',
        width: '100%',
        height: '100%',
        transition: 'transform 0.5s ease',
    },
    image: {
        width: '100%',
        height: '100%',
        flexShrink: 0,
        objectFit: 'cover',
        display: 'block',
    },
    arrow: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(15, 23, 42, 0.45)',
        color: '#fff',
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
        zIndex: 2,
    },
    dots: {
        position: 'absolute',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 8,
        zIndex: 2,
    },
    dot: {
        width: 9,
        height: 9,
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(255,255,255,0.55)',
        cursor: 'pointer',
        padding: 0,
        transition: 'background 0.15s ease, width 0.15s ease',
    },
    dotActive: {
        width: 22,
        borderRadius: 999,
        background: '#fff',
    },
};

function firstName(name) {
    if (!name) return 'Estudante';
    return name.trim().split(/\s+/)[0];
}

function formatDate(value) {
    if (!value) return '—';
    const s = String(value);
    const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (br) return `${br[1]}/${br[2]}/${br[3]}`;
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
    return '—';
}

// A validade é uma DATA (31/03): vale ATÉ O FIM do dia e só expira em 01/04.
function fimDaValidade(value) {
    if (!value) return null;
    const s = String(value);
    const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    let y, m, d;
    if (br) { d = +br[1]; m = +br[2]; y = +br[3]; }
    else if (iso) { y = +iso[1]; m = +iso[2]; d = +iso[3]; }
    else { const dt = new Date(s); if (Number.isNaN(dt.getTime())) return null; y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate(); }
    return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

function daysUntil(value) {
    const fim = fimDaValidade(value);
    if (fim == null) return null;
    return Math.ceil((fim - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function Dashboard() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isAuthenticatedUEN()) { navigate('/login', { replace: true }); return; }

        const controller = new AbortController();
        getProfileApi({ signal: controller.signal })
            .then((res) => setProfile(res.data))
            .catch((err) => {
                if (controller.signal.aborted) return;
                console.error('Erro ao carregar dashboard:', err);
                notify('Não foi possível carregar seus dados.', 'error');
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [navigate]);

    // Inferência dos blocos a partir do perfil
    const { greeting, statusInfo, pendency, quickActions } = useMemo(() => {
        const localUser = getUENUser();
        const nome = profile?.nome || localUser?.nome_completo || localUser?.nome;
        const hour = new Date().getHours();
        let saudacao = 'Olá';
        if (hour < 12) saudacao = 'Bom dia';
        else if (hour < 18) saudacao = 'Boa tarde';
        else saudacao = 'Boa noite';

        const carteirinha = profile?.carteirinha;
        const proximaAcao = carteirinha?.proxima_acao;
        const validade = carteirinha?.validade_carteirinha;
        const dias = daysUntil(validade);

        // Status visual: priorizamos status real da carteirinha; senão derivamos da
        // próxima ação (sem carteirinha -> usuário precisa comprar).
        let info;
        if (proximaAcao === 'NADA' && validade && dias > 0) {
            info = {
                tone: 'success',
                title: 'Carteirinha ativa',
                description: `Válida até ${formatDate(validade)} (${dias} dia${dias === 1 ? '' : 's'} restantes).`,
                Icon: FiCheckCircle,
            };
        } else if (validade && dias <= 0) {
            info = {
                tone: 'danger',
                title: 'Carteirinha expirada',
                description: 'Renove sua carteirinha para continuar com os benefícios.',
                Icon: FiAlertCircle,
            };
        } else if (proximaAcao === 'COMPRAR_UPGRADE_FISICA') {
            info = {
                tone: 'info',
                title: 'Adicione a versão física',
                description: 'Você já tem a digital. Complemente com a carteirinha em PVC entregue em casa.',
                Icon: FiCreditCard,
            };
        } else if (proximaAcao === 'COMPRAR_NOVA') {
            info = {
                tone: 'warning',
                title: 'Você ainda não tem carteirinha',
                description: 'Emita sua carteirinha digital ou bundle completo pra começar a usar os benefícios.',
                Icon: FiClock,
            };
        } else {
            info = {
                tone: 'muted',
                title: 'Seus documentos',
                description: 'Confira o status atual da sua carteirinha de estudante.',
                Icon: FiSmile,
            };
        }

        // Pendência (ações de cadastro/pagamento aguardando)
        const pendencias = profile?.pendencias || {};
        let pend = null;
        if (pendencias.pagamento_aguardando) {
            pend = {
                title: 'Pagamento pendente',
                description: 'Você tem um Pix aguardando pagamento. Finalize pra liberar sua carteirinha.',
                cta: { label: 'Ver pagamento', path: '/meuhistorico' },
                tone: 'warning',
            };
        } else if (pendencias.detalhes?.digital_incompleta) {
            pend = {
                title: 'Complete seus dados',
                description: 'Faltam informações no cadastro da sua carteirinha digital.',
                cta: { label: 'Preencher dados', path: '/minhacarteirinha' },
                tone: 'info',
            };
        } else if (pendencias.detalhes?.impressao_incompleta) {
            pend = {
                title: 'Endereço de entrega pendente',
                description: 'Precisamos do seu endereço pra enviar a carteirinha física.',
                cta: { label: 'Informar endereço', path: '/minhacarteirinha' },
                tone: 'info',
            };
        }

        const actions = [
            { label: 'Minha carteirinha', description: 'Visualizar e baixar', Icon: FiCreditCard, path: '/minhacarteirinha', accent: '#059669' },
            { label: 'Produtos', description: 'Comprar ou fazer upgrade', Icon: FiShoppingBag, path: '/produtos', accent: '#10B981' },
            { label: 'Histórico', description: 'Seus pagamentos', Icon: FiFileText, path: '/meuhistorico', accent: '#F59E0B' },
        ];

        return {
            greeting: { saudacao, nome: firstName(nome) },
            statusInfo: info,
            pendency: pend,
            quickActions: actions,
        };
    }, [profile]);

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner} />
                <p>Carregando sua área...</p>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* ---------- Hero (saudação) ---------- */}
            <section style={styles.hero}>
                <div style={styles.heroBlob1} />
                <div style={styles.heroBlob2} />
                <div style={styles.heroContent}>
                    <p style={styles.heroSaudacao}>{greeting.saudacao},</p>
                    <h1 style={styles.heroTitle}>{greeting.nome}! 👋</h1>
                    <p style={styles.heroSubtitle}>
                        Bem-vindo à sua área de estudante. Aqui você gerencia sua carteirinha, compra produtos e acompanha seus pagamentos.
                    </p>
                </div>
            </section>

            {/* ---------- Carrossel de banners em destaque (máx. 3) ---------- */}
            <BannerCarousel images={BANNERS} />

            {/* ---------- Propaganda: carteirinha no app (direto do bolso) ---------- */}
            <section style={styles.appPromo} data-apppromo>
                <div style={styles.appPromoGlow} />
                <div style={styles.appPromoGlow2} />
             
                <div style={styles.appPromoBody}>
                    <h3 style={styles.appPromoTitle}>Sua carteirinha no bolso</h3>
                    <p style={styles.appPromoText}>
                        Baixe o app e tenha sua carteirinha sempre à mão, direto do
                        celular — a qualquer hora, sem abrir o navegador.
                    </p>
                    <div style={styles.appPromoButtons}>
                        <div style={styles.appPromoBtnSlot}><AppleStoreButton /></div>
                        <div style={styles.appPromoBtnSlot}><GooglePlayButton /></div>
                    </div>
                </div>

                {/* Espaço pra imagem do lado direito (ex.: mockup do app no
                    celular). Quando tiver a arte, importe lá em cima e troque
                    APP_PROMO_IMG = null pelo import. */}
               {/*  <div style={styles.appPromoMedia} data-apppromo-media>
                    {APP_PROMO_IMG ? (
                        <img src={homemfeliz} alt="App da UEN no celular" style={styles.appPromoMediaImg} />
                    ) : (
                        <div style={styles.appPromoMediaPlaceholder}>imagem aqui</div>
                    )}
                </div> */}
            </section>

            {/* ---------- Pendência (se houver) ---------- */}
            {pendency && (
                <div style={{ ...styles.pendencyCard, ...pendencyTones[pendency.tone] }}>
                    <FiAlertCircle size={22} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ display: 'block', marginBottom: 2 }}>{pendency.title}</strong>
                        <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>{pendency.description}</span>
                    </div>
                    <button style={styles.pendencyCta} onClick={() => navigate(pendency.cta.path)}>
                        {pendency.cta.label} <FiArrowRight size={14} />
                    </button>
                </div>
            )}

            {/* ---------- Status + Validade ---------- */}
            <div style={styles.statusGrid}>
                <div style={{ ...styles.statusCard, ...statusToneStyles[statusInfo.tone] }}>
                    <div style={styles.statusIcon}>
                        <statusInfo.Icon size={24} />
                    </div>
                    <div>
                        <h2 style={styles.statusTitle}>{statusInfo.title}</h2>
                        <p style={styles.statusDescription}>{statusInfo.description}</p>
                    </div>
                </div>

                <div style={styles.miniCard}>
                    <FiCalendar size={20} color="#059669" />
                    <div>
                        <span style={styles.miniLabel}>Validade</span>
                        <span style={styles.miniValue}>
                            {profile?.carteirinha?.validade_carteirinha
                                ? formatDate(profile.carteirinha.validade_carteirinha)
                                : '—'}
                        </span>
                    </div>
                </div>
            </div>

            {/* ---------- Quick actions ---------- */}
            <h3 style={styles.sectionTitle}>Acessos rápidos</h3>
            <div style={styles.actionsGrid}>
                {quickActions.map((a) => (
                    <button
                        key={a.path}
                        style={styles.actionCard}
                        onClick={() => navigate(a.path)}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                        <div style={{ ...styles.actionIconWrap, backgroundColor: `${a.accent}15`, color: a.accent }}>
                            <a.Icon size={22} />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <div style={styles.actionLabel}>{a.label}</div>
                            <div style={styles.actionDescription}>{a.description}</div>
                        </div>
                        <FiArrowRight size={16} color="#94A3B8" style={{ marginLeft: 'auto' }} />
                    </button>
                ))}
            </div>

            {/* ---------- Quadro de imagem: propaganda / chamada ---------- */}
            <section style={styles.promoCard} data-promo>
                <div style={styles.promoImageWrap} data-promo-img>
                    <img
                        src={promoCardImg}
                        alt="Aproveite os benefícios da sua carteirinha UEN"
                        style={styles.promoImage}
                    />
                </div>
                <div style={styles.promoBody} data-promo-body>
                    <span style={styles.promoTag}>NOVIDADE</span>
                    <h3 style={styles.promoTitle}>Aproveite ao máximo sua carteirinha</h3>
                    <p style={styles.promoText}>
                        Use sua UEN Estudantil em cinemas, parques, shows e eventos com até <strong>50% de desconto</strong> em todo o Brasil.
                    </p>
                    <button
                        style={styles.promoCta}
                        onClick={() => navigate('/produtos')}
                    >
                        Saiba mais <FiArrowRight size={16} />
                    </button>
                </div>
            </section>

            {/* ---------- Help ---------- */}
            <section style={styles.helpCard}>
                <div style={styles.helpIconWrap}>
                    <FiMessageCircle size={22} />
                </div>
                <div style={{ flex: 1 }}>
                    <h3 style={styles.helpTitle}>Precisa de ajuda?</h3>
                    <p style={styles.helpDescription}>
                        Estamos no WhatsApp pra tirar dúvidas, ajudar com cadastro ou problemas no pagamento.
                    </p>
                </div>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={styles.helpBtn}>
                    Falar com suporte
                </a>
            </section>

            <style>{`
                @media (max-width: 720px) {
                    [data-promo] { flex-direction: column !important; }
                    [data-promo-img] {
                        width: 100% !important;
                        max-width: 100% !important;
                        min-height: 180px !important;
                    }
                    [data-promo-body] { padding: 1.25rem !important; }
                    [data-apppromo] {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        padding: 1.5rem !important;
                    }
                    [data-apppromo-media] {
                        width: 100% !important;
                        min-height: 140px !important;
                        order: 3 !important;
                    }
                }
            `}</style>
        </div>
    );
}

const pendencyTones = {
    warning: { background: '#FFFBEB', borderColor: '#FCD34D', color: '#92400E' },
    info:    { background: '#ECFDF5', borderColor: '#A7F3D0', color: '#065F46' },
    danger:  { background: '#FFF1F2', borderColor: '#FECDD3', color: '#9F1239' },
};

const statusToneStyles = {
    success: { background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', borderColor: '#A7F3D0' },
    warning: { background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)', borderColor: '#FCD34D' },
    danger:  { background: 'linear-gradient(135deg, #FFF1F2 0%, #FFFFFF 100%)', borderColor: '#FECDD3' },
    info:    { background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', borderColor: '#A7F3D0' },
    muted:   { background: '#FFFFFF', borderColor: '#E2E8F0' },
};

const styles = {
    container: {
        maxWidth: 1024,
        margin: '0 auto',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
        color: '#0F172A',
    },

    // Loading
    loading: {
        padding: '4rem 1rem', textAlign: 'center', color: '#64748B',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
    },
    spinner: {
        width: 32, height: 32,
        border: '3px solid #E2E8F0',
        borderTopColor: '#059669',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },

    // Hero
    hero: {
        position: 'relative',
        background: 'linear-gradient(135deg, #059669 0%, #0D9488 50%, #10B981 100%)',
        borderRadius: 20,
        padding: '2.25rem 2rem',
        color: '#fff',
        overflow: 'hidden',
        marginBottom: '1.5rem',
        boxShadow: '0 10px 25px -5px rgba(5, 150, 105, 0.25)',
    },
    heroBlob1: {
        position: 'absolute', top: -60, right: -40, width: 220, height: 220,
        borderRadius: '50%', background: '#10B981', filter: 'blur(60px)',
        opacity: 0.6, pointerEvents: 'none',
    },
    heroBlob2: {
        position: 'absolute', bottom: -80, left: -40, width: 180, height: 180,
        borderRadius: '50%', background: '#F59E0B', filter: 'blur(60px)',
        opacity: 0.35, pointerEvents: 'none',
    },
    heroContent: { position: 'relative', zIndex: 1, maxWidth: 560 },
    heroSaudacao: { margin: 0, fontSize: '0.95rem', opacity: 0.85, fontWeight: 500 },
    heroTitle: {
        margin: '0.2rem 0 0.6rem', fontSize: '2rem', fontWeight: 700,
        letterSpacing: '-0.02em', lineHeight: 1.1,
    },
    heroSubtitle: { margin: 0, fontSize: '0.95rem', opacity: 0.92, lineHeight: 1.55 },

    // App promo (carteirinha no bolso)
    appPromo: {
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: '1.5rem',
        background: 'linear-gradient(135deg, #047857 0%, #059669 45%, #0D9488 100%)',
        borderRadius: 24,
        padding: '2rem 2.25rem',
        marginBottom: '1.5rem',
        color: '#FFFFFF',
        boxShadow: '0 18px 40px -16px rgba(4, 120, 87, 0.6)',
    },
    appPromoGlow: {
        position: 'absolute',
        top: -90,
        right: -60,
        width: 260,
        height: 260,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)',
        pointerEvents: 'none',
    },
    appPromoGlow2: {
        position: 'absolute',
        bottom: -110,
        left: -40,
        width: 240,
        height: 240,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(13,148,136,0.45) 0%, rgba(13,148,136,0) 70%)',
        pointerEvents: 'none',
    },
    appPromoIconWrap: {
        position: 'relative',
        zIndex: 1,
        flexShrink: 0,
        width: 76,
        height: 76,
        borderRadius: 20,
        background: 'rgba(255, 255, 255, 0.16)',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.28)',
        display: 'grid',
        placeItems: 'center',
        color: '#FFFFFF',
    },
    appPromoBody: { position: 'relative', zIndex: 1, flex: 1, minWidth: 0 },
    appPromoTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        background: 'rgba(255, 255, 255, 0.18)',
        color: '#FFFFFF',
        padding: '4px 12px',
        borderRadius: 999,
        marginBottom: 10,
    },
    appPromoTitle: { margin: '0 0 6px', fontSize: '1.45rem', fontWeight: 800, letterSpacing: '-0.02em' },
    appPromoText: { margin: '0 0 18px', fontSize: '0.92rem', lineHeight: 1.55, opacity: 0.92, maxWidth: 460 },
    appPromoButtons: { display: 'flex', gap: 12, flexWrap: 'wrap' },
    appPromoBtnSlot: { width: 180, flexShrink: 0 },
    appPromoMedia: {
        position: 'relative',
        zIndex: 1,
        flexShrink: 0,
        width: 220,
        minHeight: 150,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    appPromoMediaImg: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
    },
    appPromoMediaPlaceholder: {
        width: '100%',
        minHeight: 130,
        borderRadius: 16,
        border: '2px dashed rgba(255, 255, 255, 0.45)',
        background: 'rgba(255, 255, 255, 0.08)',
        display: 'grid',
        placeItems: 'center',
        fontSize: '0.8rem',
        fontWeight: 600,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        color: 'rgba(255, 255, 255, 0.75)',
    },

    // Pendency
    pendencyCard: {
        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
        padding: '1rem 1.25rem', borderRadius: 12, marginBottom: '1.5rem',
        border: '1px solid',
    },
    pendencyCta: {
        background: '#fff', border: 'none',
        padding: '0.5rem 0.85rem', borderRadius: 8,
        fontWeight: 600, fontSize: '0.85rem',
        color: 'inherit', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
    },

    // Status grid
    statusGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
        gap: '1rem',
        marginBottom: '1.5rem',
    },
    statusCard: {
        padding: '1.25rem 1.5rem',
        borderRadius: 16,
        border: '1px solid',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.06)',
    },
    statusIcon: {
        width: 48, height: 48, borderRadius: 12,
        background: 'rgba(255, 255, 255, 0.7)',
        display: 'grid', placeItems: 'center',
        color: '#059669',
        flexShrink: 0,
    },
    statusTitle: { fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.25rem', color: '#0F172A' },
    statusDescription: { fontSize: '0.875rem', margin: 0, color: '#475569', lineHeight: 1.5 },

    miniCard: {
        padding: '1.25rem',
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.06)',
    },
    miniLabel: { display: 'block', fontSize: '0.7rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
    miniValue: { display: 'block', fontSize: '1rem', fontWeight: 700, color: '#0F172A', marginTop: 2 },

    // Section
    sectionTitle: {
        fontSize: '1rem', fontWeight: 700, color: '#0F172A',
        margin: '0.5rem 0 0.75rem',
    },

    // Actions grid
    actionsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '0.75rem',
        marginBottom: '2rem',
    },
    actionCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
        padding: '1rem 1.1rem',
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 14,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
        boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.05)',
    },
    actionIconWrap: {
        width: 42, height: 42, borderRadius: 10,
        display: 'grid', placeItems: 'center',
        flexShrink: 0,
    },
    actionLabel: { fontSize: '0.95rem', fontWeight: 600, color: '#0F172A' },
    actionDescription: { fontSize: '0.8rem', color: '#64748B', marginTop: 2 },

    // Promo card (propaganda — imagem + texto + CTA)
    promoCard: {
        display: 'flex',
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 18,
        overflow: 'hidden',
        marginBottom: '2rem',
        boxShadow: '0 4px 12px -2px rgba(15, 23, 42, 0.08)',
    },
    promoImageWrap: {
        flex: '0 0 38%',
        maxWidth: '38%',
        position: 'relative',
        background: '#F1F5F9',
        minHeight: 240,
    },
    promoImage: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    promoBody: {
        flex: 1,
        padding: '1.75rem 2rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '0.6rem',
    },
    promoTag: {
        alignSelf: 'flex-start',
        background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
        color: '#92400E',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        padding: '0.3rem 0.7rem',
        borderRadius: 999,
        textTransform: 'uppercase',
    },
    promoTitle: {
        fontSize: '1.35rem',
        fontWeight: 700,
        color: '#0F172A',
        margin: 0,
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
    },
    promoText: {
        fontSize: '0.92rem',
        color: '#475569',
        margin: 0,
        lineHeight: 1.55,
    },
    promoCta: {
        alignSelf: 'flex-start',
        marginTop: '0.4rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.7rem 1.25rem',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: 10,
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
        fontFamily: 'inherit',
    },

    // Help
    helpCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1.25rem 1.5rem',
        background: 'linear-gradient(135deg, #ECFDF5 0%, #ECFDF5 100%)',
        border: '1px solid #A7F3D0',
        borderRadius: 16,
    },
    helpIconWrap: {
        width: 44, height: 44, borderRadius: 12,
        background: '#fff', color: '#059669',
        display: 'grid', placeItems: 'center',
        flexShrink: 0,
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.2)',
    },
    helpTitle: { fontSize: '1rem', fontWeight: 700, margin: '0 0 0.2rem', color: '#0F172A' },
    helpDescription: { fontSize: '0.85rem', margin: 0, color: '#475569', lineHeight: 1.5 },
    helpBtn: {
        padding: '0.6rem 1rem',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff',
        borderRadius: 10,
        fontWeight: 600,
        fontSize: '0.85rem',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
    },
};
